// src/components/MySession.jsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, differenceInMinutes } from 'date-fns';
import { useNavigate , useLocation} from 'react-router-dom';
import { Loader2, Calendar, Clock, ChevronRight, RefreshCw, Archive, PlusCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import './MySession.css';

const COUNSELLING_API_BASE = process.env.REACT_APP_COUNSELLING_API_BASE;

const MySession = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const handleJoin = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
  const handleReschedule = (id) => navigate(`/dashboard?view=counselling&rescheduleId=${id}`);
  const handleBookNow = () => navigate(`/dashboard?view=counselling`);

  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      setError(null);
      const userId = localStorage.getItem('userId')?.trim();
      if (!userId) {
        setError('User not found. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        const { data } = await axios.get(
          `${COUNSELLING_API_BASE}/api/sessions?userId=${encodeURIComponent(userId)}`
        );
        setSessions(data);
      } catch (err) {
        console.error('Failed to fetch meetings:', err);
        setError('Could not load your sessions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, []);

  if (loading) {
    return (
      <div className="page-loader">
        <Loader2 className="animate-spin" size={48} />
        <p>Loading your sessions…</p>
      </div>
    );
  }

  if (error) {
    return <div className="page-error">{error}</div>;
  }

  const now = new Date();
  const upcoming = sessions
    .filter(m => new Date(m.dateTime) >= now)
    .sort((a, b) => new Date(a.dateTime) - new Date(b.dateTime));

  const past = sessions
    .filter(m => new Date(m.dateTime) < now)
    .sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
  
  const isStartingSoon = (meetingTime) => {
    const minutesUntil = differenceInMinutes(new Date(meetingTime), now);
    return minutesUntil >= 0 && minutesUntil <= 10;
  };

  return (
<>

 <Helmet>
          <meta name="title" content="Home | Get Next Visa - AI Powered Student Visa Report" />
        <title>My Session | Get Next Visa - AI Powered Student Visa Report</title>
        <meta name="description" content="Get personalized, AI-powered visa reports. Discover the best countries and colleges for your study abroad journey." />
      </Helmet>


    <div className="my-session-container">
      <section className="sessions-category">
        <h2 className="sessions-category-title">Upcoming Sessions</h2>
        {upcoming.length > 0 ? (
          <div className="all-meetings-list">
            {upcoming.map((m, index) => {
              const dt = new Date(m.dateTime);
              return (
                <div key={m._id} className="meeting-card-full upcoming stagger-in" style={{ animationDelay: `${index * 100}ms` }}>
                  <h3>{m.subject}</h3>
                  <div className="meeting-details">
                    <p><Calendar size={16} /> {format(dt, 'EEEE, MMM d, yyyy')}</p>
                    <p><Clock size={16} /> {format(dt, 'h:mm a')}</p>
                  </div>
                  <div className="card-actions">
                    <button className="reschedule-btn" onClick={() => handleReschedule(m._id)}>
                      <RefreshCw size={14} /> Reschedule
                    </button>
                    <button
                      className={`join-btn ${isStartingSoon(dt) ? 'pulsing' : ''}`}
                      onClick={() => handleJoin(m.zoomMeetingLink)}
                      disabled={!m.zoomMeetingLink}
                    >
                      Join Now <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-sessions-container">
            <div className="no-sessions-icon-wrapper">
                <Calendar size={28} className="no-sessions-icon"/>
            </div>
            <h3>No upcoming sessions</h3>
            <p>Ready to plan your next step? Book a new session today.</p>
            <button className="book-now-btn" onClick={handleBookNow}>
              <PlusCircle size={18} /> Book a New Session
            </button>
          </div>
        )}
      </section>

      <div className="divider"></div>

      <section className="sessions-category">
        <h2 className="sessions-category-title">Past Sessions</h2>
        {past.length > 0 ? (
          <div className="all-meetings-list">
            {past.map((m, index) => {
              const dt = new Date(m.dateTime);
              return (
                <div key={m._id} className="meeting-card-full past stagger-in" style={{ animationDelay: `${index * 100}ms` }}>
                  <h3>{m.subject}</h3>
                  <div className="meeting-details">
                    <p><Calendar size={16} /> {format(dt, 'EEEE, MMM d, yyyy')}</p>
                    <p><Clock size={16} /> {format(dt, 'h:mm a')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="no-sessions-container past-empty">
            <div className="no-sessions-icon-wrapper">
                <Archive size={28} className="no-sessions-icon"/>
            </div>
            <h3>No past sessions found</h3>
            <p>Your completed sessions will be archived here.</p>
          </div>
        )}
      </section>
    </div>
    </>
  );
};

export default MySession;